import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Avatar,
  Tooltip,
  Menu,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Videocam as VideoIcon,
  Repeat as RepeatIcon,
  MoreVert as MoreVertIcon,
  ViewWeek as WeekViewIcon,
  ViewDay as DayViewIcon,
  ViewModule as MonthViewIcon,
  Event as EventIcon,
  Circle as CircleIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { format, addDays, startOfWeek, endOfWeek, isSameDay, parseISO, addWeeks, addMonths } from 'date-fns';
import api, { handleApiError } from '../../utils/api';

interface Attendee {
  email: string;
  name: string;
  status: 'pending' | 'accepted' | 'declined' | 'tentative';
  isOrganizer?: boolean;
}

interface Reminder {
  type: 'notification' | 'email';
  time: number; // minutes before
}

interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  endDate?: string;
  endCount?: number;
  daysOfWeek?: number[]; // 0-6, Sunday-Saturday
}

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  startTime: string;
  endTime: string;
  allDay: boolean;
  description: string;
  location: string;
  videoLink: string;
  color: string;
  calendarId: string;
  attendees: Attendee[];
  reminders: Reminder[];
  recurrence?: RecurrenceRule;
  isRecurring: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  visibility: 'public' | 'private';
  createdAt: Date;
  updatedAt: Date;
}

interface Calendar {
  id: string;
  name: string;
  color: string;
  isVisible: boolean;
  isDefault: boolean;
  isOwner: boolean;
}

type ViewMode = 'month' | 'week' | 'day';

const eventColors = [
  { name: 'Blue', value: '#1976d2' },
  { name: 'Green', value: '#4caf50' },
  { name: 'Red', value: '#f44336' },
  { name: 'Orange', value: '#ff9800' },
  { name: 'Purple', value: '#9c27b0' },
  { name: 'Teal', value: '#009688' },
  { name: 'Pink', value: '#e91e63' },
  { name: 'Indigo', value: '#3f51b5' },
];

const reminderOptions = [
  { label: 'At time of event', value: 0 },
  { label: '5 minutes before', value: 5 },
  { label: '10 minutes before', value: 10 },
  { label: '15 minutes before', value: 15 },
  { label: '30 minutes before', value: 30 },
  { label: '1 hour before', value: 60 },
  { label: '2 hours before', value: 120 },
  { label: '1 day before', value: 1440 },
  { label: '1 week before', value: 10080 },
];

const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  
  const [calendars, setCalendars] = useState<Calendar[]>([]);

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const [, setLoading] = useState(true);

  const mapEvent = (e: any): CalendarEvent => ({
    ...e,
    id: e._id || e.id,
    date: new Date(e.startDate || e.date),
    createdAt: new Date(e.createdAt),
    updatedAt: new Date(e.updatedAt),
  });

  const mapCalendar = (c: any): Calendar => ({
    id: c._id || c.id,
    name: c.name,
    color: c.color,
    isVisible: c.isVisible !== false,
    isDefault: c.isDefault || false,
    isOwner: true,
  });

  const fetchCalendars = useCallback(async () => {
    try {
      const res = await api.get('/calendar/calendars');
      const data = res.data?.data || res.data || [];
      setCalendars(Array.isArray(data) ? data.map(mapCalendar) : []);
    } catch { /* ignore */ }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await api.get('/calendar/events');
      const data = res.data?.data || res.data || [];
      setEvents(Array.isArray(data) ? data.map(mapEvent) : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCalendars(), fetchEvents()]);
      setLoading(false);
    };
    init();
  }, [fetchCalendars, fetchEvents]);

  // Listen for global refresh event from header
  useEffect(() => {
    const handleRefresh = () => { fetchCalendars(); fetchEvents(); };
    window.addEventListener('app-refresh', handleRefresh);
    return () => window.removeEventListener('app-refresh', handleRefresh);
  }, [fetchCalendars, fetchEvents]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<{ anchor: HTMLElement; event: CalendarEvent } | null>(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [newCalendarColor, setNewCalendarColor] = useState('#1976d2');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success'
  });

  const [newEvent, setNewEvent] = useState<Partial<CalendarEvent>>({
    title: '',
    startTime: '09:00',
    endTime: '10:00',
    allDay: false,
    description: '',
    location: '',
    videoLink: '',
    color: '#1976d2',
    calendarId: 'default',
    attendees: [],
    reminders: [{ type: 'notification', time: 15 }],
    isRecurring: false,
    recurrence: undefined,
    status: 'confirmed',
    visibility: 'public',
  });

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getWeekDays = (date: Date) => {
    const start = startOfWeek(date, { weekStartsOn: 0 });
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  };

  const getEventsForDate = (date: Date) => {
    const visibleCalendarIds = calendars.filter(c => c.isVisible).map(c => c.id);
    return events.filter(event => 
      isSameDay(event.date, date) && visibleCalendarIds.includes(event.calendarId)
    );
  };

  const handlePrevPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, -1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    if (viewMode === 'month') {
      // Double click could open day view
    }
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setNewEvent({
      title: '',
      startTime: '09:00',
      endTime: '10:00',
      allDay: false,
      description: '',
      location: '',
      videoLink: '',
      color: '#1976d2',
      calendarId: 'default',
      attendees: [],
      reminders: [{ type: 'notification', time: 15 }],
      isRecurring: false,
      recurrence: undefined,
      status: 'confirmed',
      visibility: 'public',
    });
    setOpenDialog(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      startTime: event.startTime,
      endTime: event.endTime,
      allDay: event.allDay,
      description: event.description,
      location: event.location,
      videoLink: event.videoLink,
      color: event.color,
      calendarId: event.calendarId,
      attendees: event.attendees,
      reminders: event.reminders,
      isRecurring: event.isRecurring,
      recurrence: event.recurrence,
      status: event.status,
      visibility: event.visibility,
    });
    setOpenDialog(true);
    setMoreMenuAnchor(null);
  };

  const handleSaveEvent = async () => {
    if (!newEvent.title?.trim()) {
      setSnackbar({ open: true, message: 'Event title is required', severity: 'error' });
      return;
    }

    const eventPayload = {
      title: newEvent.title!,
      startDate: (selectedDate || new Date()).toISOString(),
      endDate: (selectedDate || new Date()).toISOString(),
      startTime: newEvent.startTime || '09:00',
      endTime: newEvent.endTime || '10:00',
      allDay: newEvent.allDay || false,
      description: newEvent.description || '',
      location: newEvent.location || '',
      videoLink: newEvent.videoLink || '',
      color: newEvent.color || '#1976d2',
      calendarId: newEvent.calendarId || 'default',
      attendees: newEvent.attendees || [],
      reminders: newEvent.reminders || [],
      isRecurring: newEvent.isRecurring || false,
      recurrence: newEvent.recurrence,
      status: newEvent.status || 'confirmed',
      visibility: newEvent.visibility || 'public',
    };

    try {
      if (editingEvent) {
        await api.put(`/calendar/events/${editingEvent.id}`, eventPayload);
        setSnackbar({ open: true, message: 'Event updated', severity: 'success' });
      } else {
        await api.post('/calendar/events', eventPayload);
        setSnackbar({ open: true, message: 'Event created', severity: 'success' });
      }
      await fetchEvents();
      setOpenDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleDeleteEvent = async (id: string) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      await fetchEvents();
      setMoreMenuAnchor(null);
      setSnackbar({ open: true, message: 'Event deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleCalendar = async (calendarId: string) => {
    const cal = calendars.find(c => c.id === calendarId);
    if (!cal) return;
    try {
      await api.put(`/calendar/calendars/${calendarId}`, { isVisible: !cal.isVisible });
      setCalendars(calendars.map(c => c.id === calendarId ? { ...c, isVisible: !c.isVisible } : c));
    } catch {
      setCalendars(calendars.map(c => c.id === calendarId ? { ...c, isVisible: !c.isVisible } : c));
    }
  };

  const handleCreateCalendar = async () => {
    if (!newCalendarName.trim()) return;
    try {
      await api.post('/calendar/calendars', { name: newCalendarName, color: newCalendarColor });
      await fetchCalendars();
      setNewCalendarName('');
      setNewCalendarColor('#1976d2');
      setCalendarDialogOpen(false);
      setSnackbar({ open: true, message: 'Calendar created', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleAddReminder = () => {
    setNewEvent({
      ...newEvent,
      reminders: [...(newEvent.reminders || []), { type: 'notification', time: 15 }]
    });
  };

  const handleCopyVideoLink = (link: string) => {
    navigator.clipboard.writeText(link);
    setSnackbar({ open: true, message: 'Video link copied', severity: 'info' });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  const isSelected = (date: Date) => {
    return selectedDate && isSameDay(date, selectedDate);
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = getWeekDays(currentDate);
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5', p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
        {/* Left Sidebar - Calendars */}
        <Paper sx={{ width: 240, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
            sx={{ mb: 2 }}
            fullWidth
          >
            New Event
          </Button>

          {/* Mini Calendar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton size="small" onClick={() => setCurrentDate(addMonths(currentDate, -1))}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={500}>
                {format(currentDate, 'MMM yyyy')}
              </Typography>
              <IconButton size="small" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, fontSize: '0.75rem' }}>
              {daysOfWeek.map(d => (
                <Typography key={d} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {d.charAt(0)}
                </Typography>
              ))}
              {getDaysInMonth(currentDate).map((date, idx) => (
                <Box
                  key={idx}
                  onClick={() => date && setSelectedDate(date)}
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    cursor: date ? 'pointer' : 'default',
                    bgcolor: date && isSelected(date) ? 'primary.main' : date && isToday(date) ? 'primary.light' : 'transparent',
                    color: date && (isSelected(date) || isToday(date)) ? 'white' : 'inherit',
                    '&:hover': date ? { bgcolor: 'action.hover' } : {},
                    fontSize: '0.75rem',
                  }}
                >
                  {date?.getDate()}
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Calendars List */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              MY CALENDARS
            </Typography>
            <IconButton size="small" onClick={() => setCalendarDialogOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <List dense disablePadding>
            {calendars.filter(c => c.isOwner).map(calendar => (
              <ListItemButton
                key={calendar.id}
                dense
                onClick={() => handleToggleCalendar(calendar.id)}
                sx={{ borderRadius: 1, py: 0.5 }}
              >
                <Checkbox
                  size="small"
                  checked={calendar.isVisible}
                  sx={{ 
                    p: 0.5, 
                    mr: 1,
                    color: calendar.color,
                    '&.Mui-checked': { color: calendar.color }
                  }}
                />
                <ListItemText 
                  primary={calendar.name} 
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            ))}
          </List>

          {calendars.some(c => !c.isOwner) && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                OTHER CALENDARS
              </Typography>
              <List dense disablePadding>
                {calendars.filter(c => !c.isOwner).map(calendar => (
                  <ListItemButton
                    key={calendar.id}
                    dense
                    onClick={() => handleToggleCalendar(calendar.id)}
                    sx={{ borderRadius: 1, py: 0.5 }}
                  >
                    <Checkbox
                      size="small"
                      checked={calendar.isVisible}
                      sx={{ 
                        p: 0.5, 
                        mr: 1,
                        color: calendar.color,
                        '&.Mui-checked': { color: calendar.color }
                      }}
                    />
                    <ListItemText 
                      primary={calendar.name} 
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Paper>

        {/* Main Calendar */}
        <Paper sx={{ flex: 2, p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Calendar Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePrevPeriod}><ChevronLeft /></IconButton>
              <IconButton onClick={handleNextPeriod}><ChevronRight /></IconButton>
              <Button variant="outlined" size="small" onClick={handleToday}>Today</Button>
              <Typography variant="h6" sx={{ ml: 2 }}>
                {viewMode === 'day' 
                  ? format(currentDate, 'EEEE, MMMM d, yyyy')
                  : viewMode === 'week'
                  ? `${format(startOfWeek(currentDate), 'MMM d')} - ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`
                  : `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                }
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Month view">
                <IconButton 
                  onClick={() => setViewMode('month')}
                  color={viewMode === 'month' ? 'primary' : 'default'}
                >
                  <MonthViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Week view">
                <IconButton 
                  onClick={() => setViewMode('week')}
                  color={viewMode === 'week' ? 'primary' : 'default'}
                >
                  <WeekViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Day view">
                <IconButton 
                  onClick={() => setViewMode('day')}
                  color={viewMode === 'day' ? 'primary' : 'default'}
                >
                  <DayViewIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Calendar Grid */}
          {viewMode === 'month' && (
            <Box sx={{ flex: 1 }}>
              {/* Days header */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e0e0e0' }}>
                {daysOfWeek.map(day => (
                  <Typography
                    key={day}
                    variant="body2"
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>
              
              {/* Days grid */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                gridAutoRows: 'minmax(100px, 1fr)',
                flex: 1,
              }}>
                {days.map((date, index) => {
                  const dayEvents = date ? getEventsForDate(date) : [];
                  return (
                    <Box
                      key={index}
                      onClick={() => date && handleDateClick(date)}
                      sx={{
                        p: 0.5,
                        border: '1px solid #e0e0e0',
                        borderTop: 'none',
                        borderLeft: index % 7 === 0 ? '1px solid #e0e0e0' : 'none',
                        bgcolor: date && isToday(date) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        cursor: date ? 'pointer' : 'default',
                        '&:hover': date ? { bgcolor: 'action.hover' } : {},
                        minHeight: 100,
                      }}
                    >
                      {date && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              bgcolor: isToday(date) ? 'primary.main' : isSelected(date) ? 'primary.light' : 'transparent',
                              color: isToday(date) || isSelected(date) ? 'white' : 'inherit',
                              fontWeight: isToday(date) ? 600 : 400,
                              mb: 0.5,
                            }}
                          >
                            {date.getDate()}
                          </Typography>
                          <Box sx={{ overflow: 'hidden' }}>
                            {dayEvents.slice(0, 3).map(event => (
                              <Box
                                key={event.id}
                                onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                                sx={{
                                  bgcolor: event.color,
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  mb: 0.25,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.9 },
                                }}
                              >
                                {event.allDay ? '' : `${event.startTime} `}{event.title}
                              </Box>
                            ))}
                            {dayEvents.length > 3 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                +{dayEvents.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {viewMode === 'week' && (
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', borderBottom: '1px solid #e0e0e0' }}>
                <Box />
                {weekDays.map(date => (
                  <Box key={date.toISOString()} sx={{ p: 1, textAlign: 'center', borderLeft: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary">
                      {format(date, 'EEE')}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        width: 36,
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday(date) ? 'primary.main' : 'transparent',
                        color: isToday(date) ? 'white' : 'inherit',
                        mx: 'auto',
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', flex: 1, overflow: 'auto' }}>
                {Array.from({ length: 24 }).map((_, hour) => (
                  <React.Fragment key={hour}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ p: 0.5, textAlign: 'right', pr: 1, borderRight: '1px solid #e0e0e0' }}
                    >
                      {hour === 0 ? '' : `${hour}:00`}
                    </Typography>
                    {weekDays.map(date => {
                      const hourEvents = getEventsForDate(date).filter(e => 
                        !e.allDay && parseInt(e.startTime.split(':')[0]) === hour
                      );
                      return (
                        <Box
                          key={`${date.toISOString()}-${hour}`}
                          sx={{
                            borderLeft: '1px solid #e0e0e0',
                            borderBottom: '1px solid #f0f0f0',
                            minHeight: 48,
                            position: 'relative',
                          }}
                        >
                          {hourEvents.map(event => (
                            <Box
                              key={event.id}
                              onClick={() => handleEditEvent(event)}
                              sx={{
                                position: 'absolute',
                                left: 2,
                                right: 2,
                                bgcolor: event.color,
                                color: 'white',
                                fontSize: '0.7rem',
                                p: 0.5,
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                zIndex: 1,
                              }}
                            >
                              {event.title}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {viewMode === 'day' && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {Array.from({ length: 24 }).map((_, hour) => {
                const hourEvents = getEventsForDate(currentDate).filter(e => 
                  !e.allDay && parseInt(e.startTime.split(':')[0]) === hour
                );
                return (
                  <Box
                    key={hour}
                    sx={{
                      display: 'flex',
                      borderBottom: '1px solid #f0f0f0',
                      minHeight: 60,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 60, p: 1, textAlign: 'right', borderRight: '1px solid #e0e0e0' }}
                    >
                      {hour === 0 ? '' : `${hour}:00`}
                    </Typography>
                    <Box sx={{ flex: 1, position: 'relative', p: 0.5 }}>
                      {hourEvents.map(event => (
                        <Box
                          key={event.id}
                          onClick={() => handleEditEvent(event)}
                          sx={{
                            bgcolor: event.color,
                            color: 'white',
                            p: 1,
                            borderRadius: 1,
                            cursor: 'pointer',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>{event.title}</Typography>
                          <Typography variant="caption">{event.startTime} - {event.endTime}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* Right Sidebar - Event Details */}
        <Paper sx={{ width: 320, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Select a date'}
          </Typography>

          {selectedDateEvents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <EventIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography>No events for this day</Typography>
              <Button 
                startIcon={<AddIcon />} 
                onClick={handleAddEvent}
                sx={{ mt: 2 }}
              >
                Create Event
              </Button>
            </Box>
          ) : (
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {selectedDateEvents.map(event => (
                <Paper key={event.id} variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: event.color, height: 4 }} />
                  <Box sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.allDay ? 'All day' : `${event.startTime} - ${event.endTime}`}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small"
                        onClick={(e) => setMoreMenuAnchor({ anchor: e.currentTarget, event })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {event.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{event.location}</Typography>
                      </Box>
                    )}

                    {event.videoLink && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <VideoIcon fontSize="small" color="primary" />
                        <Button size="small" href={event.videoLink} target="_blank">
                          Join meeting
                        </Button>
                        <IconButton size="small" onClick={() => handleCopyVideoLink(event.videoLink)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                    {event.isRecurring && (
                      <Chip
                        icon={<RepeatIcon />}
                        label={`Repeats ${event.recurrence?.frequency}`}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}

                    {event.attendees.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {event.attendees.length} attendee{event.attendees.length > 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          {event.attendees.slice(0, 5).map((attendee, idx) => (
                            <Tooltip key={idx} title={`${attendee.name} (${attendee.status})`}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                {attendee.name.charAt(0)}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {event.attendees.length > 5 && (
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.400' }}>
                              +{event.attendees.length - 5}
                            </Avatar>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      {/* Event Menu */}
      <Menu
        anchorEl={moreMenuAnchor?.anchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem onClick={() => moreMenuAnchor && handleEditEvent(moreMenuAnchor.event)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => moreMenuAnchor && handleDeleteEvent(moreMenuAnchor.event.id)}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem>
          <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Event Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Event Title"
                fullWidth
                required
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="Add title"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newEvent.allDay}
                    onChange={(e) => setNewEvent({ ...newEvent, allDay: e.target.checked })}
                  />
                }
                label="All day"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setSelectedDate(parseISO(e.target.value))}
              />
            </Grid>

            {!newEvent.allDay && (
              <>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={newEvent.startTime}
                    onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="End Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={newEvent.endTime}
                    onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Location"
                fullWidth
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                InputProps={{
                  startAdornment: <LocationIcon color="action" sx={{ mr: 1 }} />,
                }}
                placeholder="Add location"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Video Conference Link"
                fullWidth
                value={newEvent.videoLink}
                onChange={(e) => setNewEvent({ ...newEvent, videoLink: e.target.value })}
                InputProps={{
                  startAdornment: <VideoIcon color="action" sx={{ mr: 1 }} />,
                }}
                placeholder="Add video link"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Calendar</InputLabel>
                <Select
                  value={newEvent.calendarId}
                  label="Calendar"
                  onChange={(e) => setNewEvent({ ...newEvent, calendarId: e.target.value })}
                >
                  {calendars.filter(c => c.isOwner).map(cal => (
                    <MenuItem key={cal.id} value={cal.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircleIcon sx={{ fontSize: 12, color: cal.color }} />
                        {cal.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={newEvent.color}
                  label="Color"
                  onChange={(e) => setNewEvent({ ...newEvent, color: e.target.value })}
                >
                  {eventColors.map(color => (
                    <MenuItem key={color.value} value={color.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircleIcon sx={{ fontSize: 16, color: color.value }} />
                        {color.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Recurrence */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newEvent.isRecurring}
                    onChange={(e) => setNewEvent({ 
                      ...newEvent, 
                      isRecurring: e.target.checked,
                      recurrence: e.target.checked ? { frequency: 'weekly', interval: 1 } : undefined
                    })}
                  />
                }
                label="Repeat"
              />
              {newEvent.isRecurring && (
                <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={newEvent.recurrence?.frequency || 'weekly'}
                      onChange={(e) => setNewEvent({
                        ...newEvent,
                        recurrence: { ...newEvent.recurrence!, frequency: e.target.value as RecurrenceRule['frequency'] }
                      })}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Grid>

            {/* Reminders */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Reminders</Typography>
              {newEvent.reminders?.map((reminder, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <FormControl size="small" sx={{ width: 140 }}>
                    <Select
                      value={reminder.type}
                      onChange={(e) => {
                        const reminders = [...(newEvent.reminders || [])];
                        reminders[idx] = { ...reminders[idx], type: e.target.value as 'notification' | 'email' };
                        setNewEvent({ ...newEvent, reminders });
                      }}
                    >
                      <MenuItem value="notification">Notification</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={reminder.time}
                      onChange={(e) => {
                        const reminders = [...(newEvent.reminders || [])];
                        reminders[idx] = { ...reminders[idx], time: e.target.value as number };
                        setNewEvent({ ...newEvent, reminders });
                      }}
                    >
                      {reminderOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const reminders = newEvent.reminders?.filter((_, i) => i !== idx) || [];
                      setNewEvent({ ...newEvent, reminders });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddReminder}>
                Add Reminder
              </Button>
            </Grid>

            {/* Attendees */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Attendees</Typography>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={newEvent.attendees?.map(a => a.email) || []}
                onChange={(_, newValue) => {
                  const attendees = newValue.map(email => ({
                    email,
                    name: email.split('@')[0],
                    status: 'pending' as const,
                  }));
                  setNewEvent({ ...newEvent, attendees });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((email, index) => (
                    <Chip
                      avatar={<Avatar sx={{ width: 24, height: 24 }}>{email.charAt(0).toUpperCase()}</Avatar>}
                      label={email}
                      {...getTagProps({ index })}
                      size="small"
                      key={index}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Add attendees by email" size="small" />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Add description"
              />
            </Grid>

            {/* Visibility */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={newEvent.visibility}
                  label="Visibility"
                  onChange={(e) => setNewEvent({ ...newEvent, visibility: e.target.value as 'public' | 'private' })}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={newEvent.status}
                  label="Status"
                  onChange={(e) => setNewEvent({ ...newEvent, status: e.target.value as CalendarEvent['status'] })}
                >
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="tentative">Tentative</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEvent} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Calendar Dialog */}
      <Dialog open={calendarDialogOpen} onClose={() => setCalendarDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Calendar</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Calendar Name"
            value={newCalendarName}
            onChange={(e) => setNewCalendarName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Color</InputLabel>
            <Select
              value={newCalendarColor}
              label="Color"
              onChange={(e) => setNewCalendarColor(e.target.value)}
            >
              {eventColors.map(color => (
                <MenuItem key={color.value} value={color.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircleIcon sx={{ fontSize: 16, color: color.value }} />
                    {color.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateCalendar} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalendarPage;
